import { bidOnVehicle, purchaseExclusiveVehicle, purchaseVehicle } from 'apis/vehicleApis'
import {
  BidAmountTypeOptions,
  BuyItNowPrice,
  OTHER_TRANSPORT_PROVIDER_ID,
  PURCHASE_EXCLUSIVE_SUCCESS_MESSAGE,
  SELF_ARRANGED_TRANSPORT_PROVIDER_ID,
  VehicleStatuses
} from 'common/constants'
import { SlideOut } from 'components/SlideOut/SlideOut'
import { useCallback, useEffect, useState } from 'react'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'
import { IBidOnVehicleRequest } from 'types/vehicleTypes'
import shallow from 'zustand/shallow'
import { Formik, FormikHelpers } from 'formik'
import { BidAgainFormValues } from 'types/formikTypes'
import { useVehicleStore } from 'store/useVehicleStore'
import { number, object, ObjectSchema, string } from 'yup'
import { useSignalRStore } from 'store/useSignalRStore'
import { BiddingSidePanelFormContent } from './BiddingSidePanelFormContent'
import { Alert, Button, Form } from 'react-bootstrap'
import SpinnerButton from 'components/Buttons/SpinnerButton'
import { StyledSlideOutElement } from 'components/SlideOut/SlideOut.styled'
import { DismissableAlert } from 'components/DismissableAlert/DismissableAlert'
import { CountDownWithColor } from 'modules/BuyerActivity/CountDownWithColor'
import { useCountdown } from 'hooks/useCountdown'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { useLocation } from 'react-router-dom'
import { TransportationPanel } from 'modules/TransportationPanel'
import React from 'react'
import { useGlobalStore } from 'store/useGlobalStore'

const createValidationSchema = (isPurchaseExclusive: boolean): ObjectSchema<BidAgainFormValues> => {
  const baseSchema = {
    VehicleInstanceId: number().required().positive().integer(),
    BiddingDealerID: number().required().positive().integer(),
    BidAmountType: string()
      .required()
      .oneOf(['CurrentBidAmount', 'UpdateBidAmount'] as const),
    TransportCompanyID: number().required().positive().integer()
  }

  if (isPurchaseExclusive) {
    return object({
      ...baseSchema,
      // For purchase exclusive, we only need PaymentTypeID to be a valid number (can be 0)
      PaymentTypeID: number().required().integer(),
      // These fields are not required for purchase exclusive
      BidAmount: number().nullable(),
      BuyerAccountReference: string().nullable()
    })
  } else {
    return object({
      ...baseSchema,
      BidAmount: number().required('Required').positive('Required').integer(),
      PaymentTypeID: number().required().positive('Required').integer(),
      BuyerAccountReference: string()
    })
  }
}
interface IBiddingSidePanelProps {
  isDealerShipSelectable: boolean
}

export const BiddingSidePanel: React.FC<IBiddingSidePanelProps> = ({ isDealerShipSelectable }) => {
  const location = useLocation()
  const updateVehicleAuction = useVehicleStore((state) => state.updateVehicleAuction)
  const updateVehicleData = useVehicleStore((state) => state.updateVehicleData)
  const vehicles = useVehicleStore((state) => state.vehicles)
  const {
    biddingVehicleInstanceId,
    setBiddingVehicleInstanceId,
    isOpen,
    deliveryInformation,
    selectedDeliveryPrice,
    otherChildId,
    isOther,
    isBuyItNow,
    isPurchaseExclusive,
    addPurchaseExclusiveVehicle,
    hasTransportationSelected,
    hasPaymentMethodSelected,
    reset
  } = useBiddingSidePanel(
    (state) => ({
      biddingVehicleInstanceId: state.biddingVehicleInstanceId,
      setBiddingVehicleInstanceId: state.setBiddingVehicleInstanceId,
      isOpen: state.isOpen,
      deliveryInformation: state.deliveryInformation,
      selectedDeliveryPrice: state.selectedDeliveryPrice,
      otherChildId: state.otherChildId,
      isOther: state.isOther,
      isBuyItNow: state.isBuyItNow,
      isPurchaseExclusive: state.isPurchaseExclusive,
      addPurchaseExclusiveVehicle: state.addPurchaseExclusiveVehicle,
      hasPaymentMethodSelected: state.hasPaymentMethodSelected,
      reset: state.reset,
      hasTransportationSelected: state.hasTransportationSelected
    }),
    shallow
  )
  const { CurrentBuyerID, DealershipName } = useGlobalStore((state) => state.userClaims)
  const biddingVehicle = React.useMemo(
    () => vehicles?.find((vehicleData) => vehicleData.vehicle.InstanceID === biddingVehicleInstanceId),
    [vehicles, biddingVehicleInstanceId] // Recalculate when vehicles or pathname changes
  )
  const { userInteraction } = useDtmAnalytics()

  useEffect(() => {
    setBiddingVehicleInstanceId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Local state variables
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const connectionID = useSignalRStore((state) => state.connectionID)
  const [isBidSuccess, setIsBidSuccess] = useState(false)
  const [bidResponseMessage, setBidResponseMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const { seconds: timeOut } = useCountdown(biddingVehicle?.auctionInfo?.EndDate)
  const [isTransportationSlideOutOpen, setTransportationSlideOutOpen] = useState(false)

  const showBidButton =
    !isBuyItNow &&
    !isPurchaseExclusive &&
    biddingVehicle?.auctionInfo?.CanBid &&
    timeOut > 0 &&
    biddingVehicle?.vehicle?.StatusID === VehicleStatuses.OnSale

  const showBuyItNowButton =
    isBuyItNow && biddingVehicle?.auctionInfo?.CanBuy && biddingVehicle?.vehicle?.StatusID === VehicleStatuses.OnSale

  const showPurchaseExclusiveButton =
    isPurchaseExclusive && !isBidSuccess && biddingVehicle?.vehicle?.StatusID === VehicleStatuses.OnSale

  const buyItNowHandler = async (values: BidAgainFormValues, formikHelpers: FormikHelpers<BidAgainFormValues>) => {
    if (!biddingVehicle) return

    const buyRequest: IBidOnVehicleRequest = {
      VehicleInstanceId: values.VehicleInstanceId,
      ReferenceNumber: values.BuyerAccountReference,
      DeliveryQuote: {
        PaymentTypeID: values.PaymentTypeID,
        TransportCompanyID:
          values.TransportCompanyID === OTHER_TRANSPORT_PROVIDER_ID ? otherChildId : values.TransportCompanyID,
        ChannelID: biddingVehicle.vehicle.SaleChannelID,
        SalePrice: biddingVehicle.vehicle.BuyItNowPrice,
        SalesSessionStepId: biddingVehicle.vehicle.SalesSessionStepID,
        VehicleInstanceID: biddingVehicle.vehicle.InstanceID,
        AddressID: deliveryInformation?.AddressID,
        PickUpZIP: deliveryInformation?.PickUpZIP,
        Zip: ''
      },
      vehicle: biddingVehicle.vehicle
    }

    try {
      setLoading(true)
      const vehicleDataResponse = await purchaseVehicle(buyRequest, values.BiddingDealerID)

      // update auctionInfo from response
      if (vehicleDataResponse.Items?.[0]) {
        updateVehicleAuction({
          auctionInfo: vehicleDataResponse.Items[0].auctionInfo,
          vehicleInstanceID: vehicleDataResponse.Items[0].vehicle.InstanceID
        })
        updateVehicleData(vehicleDataResponse.Items?.[0])
      }

      if (buyRequest.ReferenceNumber) {
        formikHelpers.setFieldValue('BuyerAccountReference', buyRequest.ReferenceNumber)
      }

      // show response message
      setBidResponseMessage(
        vehicleDataResponse.Items?.[0]?.auctionInfo.ResponseMessage || vehicleDataResponse.ErrorMessages[0]
      )
      setIsBidSuccess(vehicleDataResponse.Success)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
    //analytics
    userInteraction(`${window.webData.pageName} : Purchase Vehicle`, [
      { auctionInfo: biddingVehicle.auctionInfo, VIN: biddingVehicle.vehicle.VIN }
    ])
  }

  const purchaseExclusiveHandler = async (
    values: BidAgainFormValues,
    formikHelpers: FormikHelpers<BidAgainFormValues>
  ) => {
    if (!biddingVehicle) return

    const purchaseExclusiveRequest: number[] = [values.VehicleInstanceId, values.PaymentTypeID, 0, 0]

    try {
      setLoading(true)
      const vehicleDataResponse = await purchaseExclusiveVehicle(purchaseExclusiveRequest)

      // show response message
      setBidResponseMessage(vehicleDataResponse)
      const isSuccess = vehicleDataResponse === PURCHASE_EXCLUSIVE_SUCCESS_MESSAGE
      setIsBidSuccess(isSuccess)

      // If purchase is successful, add the vehicle ID to the purchased vehicles list
      if (isSuccess && values.VehicleInstanceId) {
        addPurchaseExclusiveVehicle(values.VehicleInstanceId)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
    //analytics
    userInteraction(`${window.webData.pageName} : Place Bid`, [
      { auctionInfo: biddingVehicle.auctionInfo, VIN: biddingVehicle.vehicle.VIN }
    ])
  }

  const biddingHandler = async (values: BidAgainFormValues, formikHelpers: FormikHelpers<BidAgainFormValues>) => {
    if (!biddingVehicle) return

    const bidRequest: IBidOnVehicleRequest = {
      VehicleInstanceId: values.VehicleInstanceId,
      BidAmount:
        values.BidAmountType === BidAmountTypeOptions.CurrentBidAmount
          ? biddingVehicle?.auctionInfo.UserMaxBid
          : +values.BidAmount,
      BuyItNowPrice: BuyItNowPrice,
      QuickBid: false,
      ConnectionId: connectionID,
      ReferenceNumber: values.BuyerAccountReference,
      SalesSessionStepId: biddingVehicle.vehicle.SalesSessionStepID,
      DeliveryQuote: {
        PaymentTypeID: values.PaymentTypeID,
        TransportCompanyID:
          values.TransportCompanyID === OTHER_TRANSPORT_PROVIDER_ID ? otherChildId : values.TransportCompanyID,
        ChannelID: biddingVehicle.vehicle.SaleChannelID,
        SalePrice: values.BidAmount || biddingVehicle.vehicle.FinalPrice,
        SalesSessionStepId: biddingVehicle.vehicle.SalesSessionStepID,
        VehicleInstanceID: biddingVehicle.vehicle.InstanceID,
        AddressID: deliveryInformation?.AddressID,
        PickUpZIP: deliveryInformation?.PickUpZIP
      }
    }

    try {
      setLoading(true)
      const vehicleDataResponse = await bidOnVehicle(bidRequest, values.BiddingDealerID)

      // update auctionInfo from response
      if (vehicleDataResponse.Items?.[0]?.auctionInfo) {
        formikHelpers.setFieldValue('BidAmount', vehicleDataResponse.Items[0].auctionInfo.NextBidAmount)
        updateVehicleAuction({
          auctionInfo: vehicleDataResponse.Items[0].auctionInfo,
          vehicleInstanceID: vehicleDataResponse.Items[0].vehicle.InstanceID
        })
      }

      if (bidRequest.ReferenceNumber) {
        formikHelpers.setFieldValue('BuyerAccountReference', bidRequest.ReferenceNumber)
      }

      // show response message
      setBidResponseMessage(
        vehicleDataResponse.Items?.[0]?.auctionInfo.ResponseMessage || vehicleDataResponse.ErrorMessages[0]
      )
      setIsBidSuccess(vehicleDataResponse.Success)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
    //analytics
    userInteraction(`${window.webData.pageName} : Place Bid`, [
      { auctionInfo: biddingVehicle.auctionInfo, VIN: biddingVehicle.vehicle.VIN }
    ])
  }
  const resetSidePanel = () => {
    setBiddingVehicleInstanceId(null)
    setBidResponseMessage('')
    setIsAddressModalOpen(false)
    setTransportationSlideOutOpen(false)
    setIsBidSuccess(false)
    reset()
  }
  const onContinueClick = () => {
    resetSidePanel()
    if (window.location.pathname.startsWith('/vehicle/')) {
      window.location.reload()
    }
  }
  const handleOpenAddressModal = (isOpen: boolean) => {
    setIsAddressModalOpen(isOpen)
  }
  const openTransportationSlideOut = useCallback(() => setTransportationSlideOutOpen(true), [])
  const closeTransportationSlideOut = useCallback(() => {
    setIsAddressModalOpen(false)
    setTransportationSlideOutOpen(false)
  }, [])
  if (!isOpen) return null
  return (
    <SlideOut
      isOpen={isOpen}
      onCloseButtonClick={() => {
        resetSidePanel()
      }}
      isTransportationSlideOutOpen={isTransportationSlideOutOpen}
    >
      <Formik
        data-testid="form"
        initialValues={{
          VehicleInstanceId: biddingVehicle?.vehicle.InstanceID,
          BidAmount: biddingVehicle?.auctionInfo?.NextBidAmount,
          PaymentTypeID: 0,
          BiddingDealerID: CurrentBuyerID,
          BidAmountType: BidAmountTypeOptions.UpdateBidAmount,
          TransportCompanyID: SELF_ARRANGED_TRANSPORT_PROVIDER_ID,
          BuyerAccountReference: null
        }}
        onSubmit={(values, formikHelpers) => {
          if (isPurchaseExclusive) {
            return purchaseExclusiveHandler(values, formikHelpers)
          } else if (isBuyItNow) {
            return buyItNowHandler(values, formikHelpers)
          } else {
            return biddingHandler(values, formikHelpers)
          }
        }}
        validationSchema={createValidationSchema(isPurchaseExclusive)}
      >
        {(props) => (
          <Form className="form" onSubmit={props.handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
              {isTransportationSlideOutOpen && (
                <div className="transport-container">
                  <TransportationPanel
                    isAddressModalOpen={isAddressModalOpen}
                    onOpenAddressModal={handleOpenAddressModal}
                  ></TransportationPanel>
                </div>
              )}
              <div className="panel-container">
                <BiddingSidePanelFormContent
                  isDealerShipSelectable={isDealerShipSelectable}
                  setFetchLoading={setLoading}
                  openTransportationSlideOut={openTransportationSlideOut}
                  closeTransportationSlideOut={closeTransportationSlideOut}
                  isBidSuccess={isBidSuccess}
                />
                {CurrentBuyerID !== biddingVehicle?.auctionInfo?.BuyerID && !isPurchaseExclusive && !isBuyItNow && (
                  <div
                    style={{
                      backgroundColor: '#fcf8e3',
                      borderColor: '#faebcc',
                      color: '#8a6d3b',
                      padding: '15px',
                      border: '1px solid transparent'
                    }}
                  >
                    {`By clicking Update, you will be transferring your previously entered bid from ${biddingVehicle?.auctionInfo?.BuyerName} to ${DealershipName}`}
                  </div>
                )}
                <DismissableAlert
                  variant={isBidSuccess ? 'success' : 'danger'}
                  message={bidResponseMessage}
                  onClose={() => setBidResponseMessage('')}
                  autoDismissable={5000}
                />
                {!isPurchaseExclusive && (
                  <Alert show={biddingVehicle?.auctionInfo.NextBidAmount > props.values.BidAmount} variant="danger">
                    Current bid amount has changed
                  </Alert>
                )}

                <StyledSlideOutElement className="action-buttons">
                  {(showBidButton || showPurchaseExclusiveButton) && (
                    <CountDownWithColor auctionInfo={biddingVehicle.auctionInfo} vehicleInfo={biddingVehicle.vehicle} />
                  )}
                  <div className="button-group">
                    {showBidButton && (
                      <SpinnerButton
                        data-testid={'bidButton'}
                        isLoading={loading}
                        type="submit"
                        className="mr-3 form-btn"
                        variant="primary"
                        disabled={
                          !hasPaymentMethodSelected ||
                          (isOther && !selectedDeliveryPrice) ||
                          (!biddingVehicle?.auctionInfo.HasPreviousBid && !hasTransportationSelected && !isOther) ||
                          (biddingVehicle?.auctionInfo.HasPreviousBid &&
                            biddingVehicle?.auctionInfo.BuyerID !== CurrentBuyerID &&
                            !hasTransportationSelected &&
                            !isOther)
                        }
                      >
                        {biddingVehicle?.auctionInfo.HasPreviousBid ? 'Update' : 'Bid'}
                      </SpinnerButton>
                    )}
                    {(showBuyItNowButton || showPurchaseExclusiveButton) && (
                      <SpinnerButton
                        data-testid={'buyItNowButton'}
                        isLoading={loading}
                        type="submit"
                        className="mr-3 form-btn"
                        variant="primary"
                        disabled={
                          !hasPaymentMethodSelected ||
                          (isBuyItNow
                            ? (isOther && !selectedDeliveryPrice) || (!hasTransportationSelected && !isOther)
                            : isOther)
                        }
                      >
                        Buy
                      </SpinnerButton>
                    )}
                    {isBidSuccess && (isPurchaseExclusive || isBuyItNow) && (
                      <Button variant="primary" className="form-btn mr-3" onClick={onContinueClick}>
                        Continue
                      </Button>
                    )}
                    {(!isBidSuccess || (!isPurchaseExclusive && !isBuyItNow)) && (
                      <Button variant="light form-btn" onClick={() => resetSidePanel()}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </StyledSlideOutElement>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </SlideOut>
  )
}
